import {
  HttpHandler,
  HttpRequest,
  HttpEvent,
  HttpErrorResponse,
  HttpInterceptor,
} from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { ErrorDialogService } from "../../shared/errors/error-dialog.service";
import { LoadingBarService } from "../../shared/loading/loading-bar.service";
import { Injectable } from "@angular/core";
import { AuthService } from "../auth/auth.service";

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private errorDialogService: ErrorDialogService,
    private LoadingBarService: LoadingBarService,
    private authService: AuthService
  ) {}

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    this.LoadingBarService.show();
    // The pipe is cast to an HttpEvent stream at its end, since a 401, a 403
    // or an aborted request emits the HttpErrorResponse itself (`of(errorResp)`
    // below), which is no HttpEvent. HttpClient lets only an HttpResponse
    // through to a caller that observes the body or the response, so that
    // caller's request completes without a value or an error.
    return next.handle(request).pipe(
      catchError((errorResp: HttpErrorResponse) => {
        console.error("Error from error interceptor", errorResp);

        // cctool-go stamps X-Request-Id on every response; surfacing it in the
        // dialog turns a user's screenshot into a backend log lookup
        // (console-automatic-upgrade). Absent on legacy /apiv2 and
        // BFF-originated errors, in which case the dialog omits the line.
        const requestId = errorResp.headers?.get("X-Request-Id") ?? undefined;

        // AI: SSE connection errors are expected during development (e.g. backend restarts)
        // and should not trigger a user-facing error dialog. Let sse-client handle it.
        if (request.url.includes("/notifications/subscribe")) {
          return throwError(errorResp);
        }

        // AI: Abort errors can happen for a variety of reasons, e.g. when the user
        // navigates away from a page while a request is in flight. We don't
        // want to show an error dialog for these. The error property can be the
        // string "abort" or an object with a type property "abort".
        if (errorResp.error?.type === 'abort' || errorResp.error === 'abort') {
          return of(errorResp);
        }

        switch (errorResp.status) {
          case 401: {
            if (errorResp.url?.includes("/apiv2/")) {
              setTimeout(() => {
                this.errorDialogService.openDialog(
                  "Our Cool backend is having problems. Please report the issue to Slack",
                  undefined,
                  requestId
                );
              });
            } else {
              console.log("401 need login");
              //this.router.navigate(['login']);
              this.authService.login();
              //window.location.assign('/auth/login?returnUrl='+ this.router.url);
            }

            return of(errorResp);
          }

          case 403: {
            // AI: Show a clear "access denied" message instead of triggering login.
            // AI: The user is authenticated but lacks permission for this action.
            setTimeout(() => {
              this.errorDialogService.openDialog(
                errorResp.error?.message ||
                  "You do not have permission to perform this action.",
                errorResp.status,
                requestId,
              );
            });
            return of(errorResp);
          }

          case 0: {
            // let other event types flow through
          }
        }

        if (errorResp.error && typeof errorResp.error.message === 'string') {
          console.log('api error', errorResp);
          // Structured errors with error_code: let the calling component handle them.
          if (errorResp.error.error_code) {
            return throwError(errorResp);
          }
          setTimeout(() => {
            this.errorDialogService.openErrorDialog(errorResp.error, errorResp.status, requestId);
          });
          return throwError(errorResp);
        }

        console.log('other error', errorResp);

        let errorMsg =
          errorResp.message !== null && errorResp.message !== undefined
            ? errorResp.message
            : JSON.stringify(errorResp);
        if (errorResp.error !== null && errorResp.error !== undefined) {
          if (
            errorResp.error.message !== null &&
            errorResp.error.message !== undefined
          ) {
            errorMsg = errorResp.error.message;
          } else if (
            errorResp.error.exceptionMessage !== null &&
            errorResp.error.exceptionMessage !== undefined
          ) {
            errorMsg = errorResp.error.exceptionMessage;
          }
        }
        setTimeout(() => {
          this.errorDialogService.openDialog(errorMsg, errorResp.status, requestId);
        });
        return throwError(errorResp);
      }),
      finalize(() => {
        this.LoadingBarService.hide();
      })
    ) as Observable<HttpEvent<unknown>>;
  }
}

/*
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    this.LoadingBarService.show();
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error from error interceptor', error);

        let errorMsg = error.message !== null && error.message !== undefined ? error.message : JSON.stringify(error);
        if (error.error !== null && error.error !== undefined) {
          if (error.error.message !== null && error.error.message !== undefined )  {
            errorMsg = error.error.message;
          } else if (error.error.exceptionMessage !== null && error.error.exceptionMessage !== undefined )  {
            errorMsg = error.error.exceptionMessage;
          }
        }
        setTimeout(() => {
          this.errorDialogService.openDialog(errorMsg, error.status);
        });
        return throwError(error);
      }),
      finalize(() => {
        this.LoadingBarService.hide();
      })
    ) as Observable<HttpEvent<any>>;
  }
*/
